
/**
 * zero-price-contact.js
 *
 * - Opens/closes the zero-price contact modal
 * - Moves modal to body to avoid stacking issues
 * - Allows normal Shopify contact submission for CAPTCHA
 * - Saves and restores form drafts after redirects
 * - Recognizes Shopify's successful submission response
 * - Recovers confirmed mobile homepage redirects
 * - Resets form when reopened after success
 * - Cleans up Swym wishlist injections
 *
 * Independent from product-inquiry.js and sold-out-inquiry.js
 */

(function () {
  const DRAFT_KEY = "zeroPriceContactDraft";
  const PENDING_KEY = "zeroPriceContactPending";
  const RETURN_URL_KEY = "zeroPriceContactReturnUrl";
  const MODAL_ID_KEY = "zeroPriceContactModalId";

  /*
   * Recover successful inquiries that Shopify
   * unexpectedly redirects to the homepage.
   */
  (function recoverHomepageRedirect() {
    try {
      const pending = sessionStorage.getItem(PENDING_KEY);
      if (pending !== "1") return;

      const currentUrl = new URL(window.location.href);

      if (
        currentUrl.pathname !== "/" ||
        currentUrl.searchParams.get("contact_posted") !== "true"
      ) {
        return;
      }

      const savedUrl = sessionStorage.getItem(RETURN_URL_KEY);
      if (!savedUrl) return;

      const productUrl = new URL(
        savedUrl,
        window.location.origin
      );

      // Only recover product pages on this storefront.
      if (
        productUrl.origin !== window.location.origin ||
        !/(^|\/)products\/[^/]+/.test(productUrl.pathname)
      ) {
        return;
      }

      // Preserve Shopify's success confirmation.
      productUrl.searchParams.set(
        "contact_posted",
        "true"
      );

      productUrl.hash = "contact_form";

      window.location.replace(productUrl.toString());

    } catch (error) {
      console.error(
        "Zero-price inquiry redirect recovery:",
        error
      );
    }
  })();

  function ensureModalInBody(modal) {
    if (!modal) return;
    if (modal.parentElement === document.body) return;

    const placeholder = document.createComment(
      "zero-price-contact-modal-placeholder"
    );

    modal._zeroPriceContactPlaceholder = placeholder;

    modal.parentNode.insertBefore(
      placeholder,
      modal
    );

    document.body.appendChild(modal);
  }

  function restoreModalToPlaceholder(modal) {
    if (!modal) return;

    const placeholder = modal._zeroPriceContactPlaceholder;

    if (placeholder && placeholder.parentNode) {
      placeholder.parentNode.insertBefore(
        modal,
        placeholder
      );

      placeholder.parentNode.removeChild(placeholder);
    }

    modal._zeroPriceContactPlaceholder = null;
  }

  function clearDraft() {
    try {
      sessionStorage.removeItem(DRAFT_KEY);
      sessionStorage.removeItem(PENDING_KEY);
      sessionStorage.removeItem(RETURN_URL_KEY);
      sessionStorage.removeItem(MODAL_ID_KEY);
    } catch (_) {}
  }

  function fixSwymInModal(modal) {
    if (!modal) return;

    modal
      .querySelectorAll(".swym-atw-button-container")
      .forEach((el) => el.remove());

    const submit = modal.querySelector(
      "[data-zero-price-submit]"
    );

    const actions = modal.querySelector(
      ".zero-price-modal__actions"
    );

    if (submit && actions && !actions.contains(submit)) {
      actions.appendChild(submit);
    }
  }

  function setModalOpen(modal) {
    if (!modal) return;

    modal.removeAttribute("hidden");
    modal.setAttribute("aria-hidden", "false");

    document.documentElement.classList.add(
      "zero-price-modal-open"
    );

    document.body.classList.add(
      "zero-price-modal-open"
    );
  }

  function setModalClosed(modal) {
    if (!modal) return;

    modal.setAttribute("hidden", "hidden");
    modal.setAttribute("aria-hidden", "true");

    document.documentElement.classList.remove(
      "zero-price-modal-open"
    );

    document.body.classList.remove(
      "zero-price-modal-open"
    );
  }

  function ensureCaptchaNote(modal) {
    if (!modal) return;

    let note = modal.querySelector(
      "[data-zero-price-captcha-note]"
    );

    if (note) return;

    note = document.createElement("p");

    note.setAttribute(
      "data-zero-price-captcha-note",
      ""
    );

    note.className = "zero-price-modal__captcha-note";

    note.textContent =
      "If you get a CAPTCHA, your inquiry will be sent once it’s completed successfully.";

    const actions = modal.querySelector(
      ".zero-price-modal__actions"
    );

    if (actions && actions.parentNode) {
      actions.parentNode.insertBefore(
        note,
        actions.nextSibling
      );
    } else {
      modal.appendChild(note);
    }
  }

  function clearInlineErrors(modal) {
    if (!modal) return;

    modal
      .querySelectorAll("[data-zero-price-error]")
      .forEach((el) => el.remove());
  }

  function showInlineMessage(modal, message) {
    if (!modal) return;

    let node = modal.querySelector(
      "[data-zero-price-error]"
    );

    if (!node) {
      node = document.createElement("p");

      node.setAttribute(
        "data-zero-price-error",
        ""
      );

      node.className = "zero-price-modal__error";

      const actions = modal.querySelector(
        ".zero-price-modal__actions"
      );

      if (actions && actions.parentNode) {
        actions.parentNode.insertBefore(
          node,
          actions.nextSibling
        );
      } else {
        const form = modal.querySelector("form");

        if (form) {
          form.appendChild(node);
        } else {
          modal.appendChild(node);
        }
      }
    }

    node.textContent = message;
  }

  function saveDraft(form) {
    try {
      const fd = new FormData(form);
      const saved = {};

      for (const [key, value] of fd.entries()) {
        if (typeof value === "string") {
          saved[key] = value;
        }
      }

      const modal = form.closest(".zero-price-modal");

      if (modal && modal.id) {
        sessionStorage.setItem(
          MODAL_ID_KEY,
          modal.id
        );
      }

      sessionStorage.setItem(
        DRAFT_KEY,
        JSON.stringify(saved)
      );

      sessionStorage.setItem(PENDING_KEY, "1");

      sessionStorage.setItem(
        RETURN_URL_KEY,
        window.location.href
      );

    } catch (_) {}
  }

  function restoreDraftIntoModal(modal) {
    if (!modal) return;

    try {
      const raw = sessionStorage.getItem(DRAFT_KEY);
      if (!raw) return;

      const draft = JSON.parse(raw);

      if (!draft || typeof draft !== "object") {
        return;
      }

      Object.keys(draft).forEach((name) => {
        const field = Array.from(
          modal.querySelectorAll("[name]")
        ).find((el) => el.name === name);

        if (field && "value" in field) {
          // Do not overwrite Shopify's hidden fields.
          if (field.type === "hidden") return;

          field.value = draft[name];
        }
      });

    } catch (_) {}
  }

  function resetSubmitButton(modal) {
    if (!modal) return;

    const submitBtn = modal.querySelector(
      "[data-zero-price-submit]"
    );

    if (!submitBtn) return;

    submitBtn.disabled = false;
    submitBtn.removeAttribute("aria-busy");
    submitBtn.textContent = "Submit Inquiry";
  }

  function showForm(modal) {
    if (!modal) return;

    const formWrap = modal.querySelector(
      "[data-zero-price-form]"
    );

    const successWrap = modal.querySelector(
      "[data-zero-price-success]"
    );

    if (formWrap) {
      formWrap.hidden = false;
    }

    if (successWrap) {
      successWrap.hidden = true;
    }

    resetSubmitButton(modal);
  }

  function showSuccess(modal) {
    if (!modal) return;

    const formWrap = modal.querySelector(
      "[data-zero-price-form]"
    );

    const successWrap = modal.querySelector(
      "[data-zero-price-success]"
    );

    if (formWrap) {
      formWrap.hidden = true;
    }

    if (successWrap) {
      successWrap.hidden = false;
    }

    resetSubmitButton(modal);
  }

  function resetModalForm(modal) {
    if (!modal) return;

    const formWrap = modal.querySelector(
      "[data-zero-price-form]"
    );

    const successWrap = modal.querySelector(
      "[data-zero-price-success]"
    );

    // Clear fields only when reopening after success.
    const wasSuccessful =
      successWrap && !successWrap.hidden;

    if (wasSuccessful && formWrap) {
      const form = formWrap.querySelector("form");

      if (form) {
        // Restores default values, including the
        // prefilled product purchase message.
        form.reset();
      }
    }

    showForm(modal);
    clearInlineErrors(modal);
  }

  function openModal(modal, resetView = true) {
    if (!modal) return;

    ensureModalInBody(modal);

    if (resetView) {
      showForm(modal);
    }

    setModalOpen(modal);
    clearInlineErrors(modal);
    ensureCaptchaNote(modal);
    fixSwymInModal(modal);

    let tries = 0;

    const interval = setInterval(() => {
      fixSwymInModal(modal);
      tries += 1;

      if (tries >= 10) {
        clearInterval(interval);
      }
    }, 100);
  }

  function closeModal(modal) {
    if (!modal) return;

    setModalClosed(modal);
    restoreModalToPlaceholder(modal);
  }

  function restoreAfterCaptcha() {
    try {
      const pending = sessionStorage.getItem(
        PENDING_KEY
      );

      if (pending !== "1") return;

      const modalId = sessionStorage.getItem(
        MODAL_ID_KEY
      );

      const modal = modalId
        ? document.getElementById(modalId)
        : document.querySelector(".zero-price-modal");

      // Preserve pending state if modal is not available.
      if (!modal) return;

      const params = new URLSearchParams(
        window.location.search
      );

      const urlConfirmed =
        params.get("contact_posted") === "true";

      // Check Shopify's native Liquid success marker.
      const liquidConfirmed = !!modal.querySelector(
        "[data-zero-price-confirmed]"
      );

      const successConfirmed =
        urlConfirmed || liquidConfirmed;

      // Confirm that we returned to the original
      // product page before restoring the popup.
      const returnUrl = sessionStorage.getItem(
        RETURN_URL_KEY
      );

      if (returnUrl) {
        const original = new URL(returnUrl);
        const current = new URL(window.location.href);

        if (
          original.origin !== current.origin ||
          original.pathname !== current.pathname
        ) {
          return;
        }
      }

      openModal(modal, true);

      if (successConfirmed) {
        showSuccess(modal);
        clearDraft();

        // Remove Shopify's success parameter
        // without refreshing the page.
        if (urlConfirmed) {
          try {
            const url = new URL(
              window.location.href
            );

            url.searchParams.delete(
              "contact_posted"
            );

            window.history.replaceState(
              {},
              "",
              url.toString()
            );
          } catch (_) {}
        }

      } else {
        restoreDraftIntoModal(modal);

        showInlineMessage(
          modal,
          "Your inquiry has not been confirmed as sent. Please review your information and submit again if necessary."
        );

        try {
          sessionStorage.removeItem(PENDING_KEY);
          sessionStorage.removeItem(MODAL_ID_KEY);
        } catch (_) {}
      }

      resetSubmitButton(modal);

    } catch (error) {
      console.error(
        "Zero-price inquiry restoration error:",
        error
      );
    }
  }

  // Wait until the page HTML is available.
  if (document.readyState === "loading") {
    document.addEventListener(
      "DOMContentLoaded",
      restoreAfterCaptcha
    );
  } else {
    restoreAfterCaptcha();
  }

  // Open and close the modal.
  document.addEventListener("click", function (e) {
    const openBtn = e.target.closest(
      "[data-zero-price-contact-open]"
    );

    if (openBtn) {
      e.preventDefault();

      const id = openBtn.getAttribute(
        "aria-controls"
      );

      const modal = id
        ? document.getElementById(id)
        : null;

      if (modal) {
        resetModalForm(modal);
        openModal(modal);
      }

      return;
    }

    const closeBtn = e.target.closest(
      "[data-zero-price-contact-close]"
    );

    if (closeBtn) {
      e.preventDefault();

      const modal = closeBtn.closest(
        ".zero-price-modal"
      );

      closeModal(modal);
    }
  });

  // Escape key closes modal.
  document.addEventListener("keydown", function (e) {
    if (e.key !== "Escape") return;

    document
      .querySelectorAll(".zero-price-modal:not([hidden])")
      .forEach(closeModal);
  });

  // Shopify theme editor safety.
  document.addEventListener(
    "shopify:section:unload",
    function () {
      document
        .querySelectorAll(
          ".zero-price-modal:not([hidden])"
        )
        .forEach(closeModal);
    }
  );

  // Handle contact form submission.
  document.addEventListener("submit", function (e) {
    const form = e.target;

    if (!(form instanceof HTMLFormElement)) {
      return;
    }

    const modal = form.closest(
      ".zero-price-modal"
    );

    if (!modal) return;

    const submitBtn = form.querySelector(
      "[data-zero-price-submit]"
    );

    if (!submitBtn) return;

    clearInlineErrors(modal);
    ensureCaptchaNote(modal);
    fixSwymInModal(modal);

    // Validate all required fields.
    if (!form.checkValidity()) {
      e.preventDefault();
      form.reportValidity();
      return;
    }

    // Save the form before Shopify navigation.
    saveDraft(form);

    // Reduce accidental duplicate submissions.
    submitBtn.disabled = true;
    submitBtn.setAttribute(
      "aria-busy",
      "true"
    );

    submitBtn.textContent = "Submitting…";

    // Allow Shopify's normal submission and CAPTCHA.
  });
})();
